import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageCircle, MapPin, ChevronDown, Phone, Mail, Instagram, Compass, Mountain, Waves, Trees, Footprints, Landmark, Tent, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import DestinationCard from "@/components/DestinationCard";
import ErtigaSection from "@/components/ErtigaSection";
import { useEnquiry } from "@/components/Enquiry";
import { FEATURED_12, CATEGORIES, ITINERARIES, DESTINATIONS } from "@/data/destinations";
import { IMG, img } from "@/data/images";
import { BUSINESS, waLink, telLink, mailLink } from "@/data/business";

const COUNTS = DESTINATIONS.reduce((a, d) => {
  (d.categories || []).forEach((c) => (a[c] = (a[c] || 0) + 1));
  return a;
}, {});

const CAT_ICONS = { religious: Sparkles, hills: Mountain, lakes: Waves, wildlife: Trees, adventure: Footprints, itinerary: Tent, heritage: Landmark };

const HOME_SEO = {
  title: "Tingadya Travels — Uttarakhand Taxi & Destination Travel Guide",
  description: "Premium Ertiga taxi journeys across Uttarakhand — Char Dham Yatra, hill stations, lakes, wildlife and treks. Plan your trip with phone, WhatsApp and email enquiries.",
};

const MARQUEE_ITEMS = ["Kedarnath", "Badrinath", "Valley of Flowers", "Mussoorie", "Nainital", "Auli", "Rishikesh", "Haridwar", "Chopta", "Tehri Lake", "Jim Corbett", "Munsiyari", "Hemkund Sahib", "Gangotri", "Yamunotri", "Binsar"];

function Hero() {
  const { openEnquiry } = useEnquiry();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="home-hero">
      <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
        <img
          src={img(IMG.ertiga_hero[0], 1800)}
          alt="White Ertiga taxi of Tingadya Travels on a winding Himalayan mountain road in Uttarakhand"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-transparent" />
      </motion.div>
      <motion.div style={{ opacity: fade }} className="container-x relative z-10 pb-20 pt-40 sm:pb-28">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="eyebrow flex items-center gap-3">
          <span className="h-px w-10 bg-gold" /> Tingadya Travels · Uttarakhand
        </motion.p>
        <h1 className="mt-5 font-display text-[2.6rem] font-bold leading-[1.05] text-white sm:text-6xl lg:text-7xl" data-testid="hero-heading">
          <span className="hero-line"><span style={{ animationDelay: "0.25s" }}>Sacred Peaks.</span></span>
          <span className="hero-line"><span style={{ animationDelay: "0.45s" }}>Silent Lakes.</span></span>
          <span className="hero-line"><span className="text-gold-gradient" style={{ animationDelay: "0.65s" }}>One Perfect Drive.</span></span>
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.95 }} className="mt-6 max-w-xl text-base leading-relaxed text-snow/85 sm:text-lg">
          Premium Ertiga taxi journeys across Uttarakhand — Char Dham Yatra, hill stations, lakes, wildlife and trekking getaways, planned by people who call these mountains home.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.1 }} className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <Link to="/explore" data-testid="hero-explore-button" className="btn-gold sm:w-auto">Explore Destinations <ArrowRight size={16} /></Link>
          <button onClick={() => openEnquiry("", "Hero")} data-testid="hero-plan-trip-button" className="btn-white sm:w-auto">Plan Your Trip</button>
          <a href={waLink("Hello Tingadya Travels! I want to plan a Uttarakhand trip.")} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-button" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#25D366]/60 bg-[#25D366]/10 px-6 py-3 text-sm font-bold text-[#4be38b] backdrop-blur-sm transition-all hover:bg-[#25D366]/20 active:scale-95">
            <MessageCircle size={16} /> WhatsApp Tingadya Travels
          </a>
        </motion.div>
      </motion.div>
      <motion.div style={{ opacity: fade }} className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-gold/80" aria-hidden="true">
        <ChevronDown size={22} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

export default function Home() {
  const { openEnquiry } = useEnquiry();
  return (
    <>
      <Seo {...HOME_SEO} />
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />

      {/* Featured destinations */}
      <section className="section-pad" data-testid="featured-destinations-section">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <p className="eyebrow">Uttarakhand Destination Guide</p>
                <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">Discover the Beauty of Uttarakhand</h2>
                <p className="mt-4 leading-relaxed text-mist">Sacred temples, peaceful hill stations, crystal-clear lakes and unforgettable Himalayan journeys.</p>
              </div>
              <Link to="/explore" data-testid="view-all-destinations-button" className="btn-ghost-gold w-fit shrink-0">
                View All Destinations <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {FEATURED_12.map((d, i) => (
              <Reveal key={d.slug} delay={(i % 4) * 0.07}>
                <DestinationCard d={d} testid={`featured-card-${d.slug}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Char Dham band */}
      <section className="relative overflow-hidden" data-testid="char-dham-teaser">
        <img src={img(IMG.chardham_banner[0], 1800)} alt="Himalayan temple with prayer flags above a Garhwal valley at dawn" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/75" />
        <div className="container-x relative z-10 py-20 sm:py-28">
          <Reveal>
            <p className="eyebrow">The Sacred Circuit</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">Char Dham Yatra — Planned End to End</h2>
            <p className="mt-4 max-w-2xl leading-relaxed text-snow/85">
              Yamunotri, Gangotri, Kedarnath and Badrinath in one carefully paced journey. We handle the route, night halts, trek bases and registrations guidance — you keep the darshan.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              {["Yamunotri", "Gangotri", "Kedarnath", "Badrinath"].map((d) => (
                <span key={d} className="chip border-gold/40 bg-black/50 text-gold" data-testid={`chardham-chip-${d.toLowerCase()}`}>{d}</span>
              ))}
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link to="/char-dham" data-testid="char-dham-details-button" className="btn-gold">Char Dham Yatra Details <ArrowRight size={16} /></Link>
              <button onClick={() => openEnquiry("Char Dham Yatra", "Home Char Dham band", { tripDays: "Customised trip" })} data-testid="plan-my-yatra-button" className="btn-white">Plan My Yatra</button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Banner CTA */}
      <section className="relative overflow-hidden py-24 sm:py-32" data-testid="journey-banner">
        <div className="absolute inset-0">
          <img src={img(IMG.himalaya_road[0], 1800)} alt="Scenic Himalayan road winding through the Uttarakhand mountains" loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/30" />
        </div>
        <div className="container-x relative z-10">
          <Reveal>
            <h2 className="max-w-3xl font-display text-3xl font-bold leading-tight text-white sm:text-5xl">Your Journey Through Uttarakhand Starts Here</h2>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link to="/explore" data-testid="banner-explore-button" className="btn-gold">Explore Destinations</Link>
              <button onClick={() => openEnquiry("", "Journey banner")} data-testid="banner-plan-trip-button" className="btn-white">Plan Your Trip</button>
              <a href={waLink("Hello Tingadya Travels! I want to plan a Uttarakhand trip.")} target="_blank" rel="noopener noreferrer" data-testid="banner-whatsapp-button" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#25D366]/60 bg-black/40 px-6 py-3 text-sm font-bold text-[#4be38b] backdrop-blur-sm transition-all hover:bg-[#25D366]/20">
                <MessageCircle size={16} /> WhatsApp Tingadya Travels
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Categories */}
      <section className="section-pad" data-testid="categories-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Browse by Mood</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">Every Kind of Mountain Journey</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {CATEGORIES.map((c, i) => {
              const Icon = CAT_ICONS[c.id] || Mountain;
              return (
                <Reveal key={c.id} delay={i * 0.05}>
                  <Link to={`/explore?category=${c.id}`} data-testid={`home-category-${c.id}`} className="group flex h-full flex-col gap-6 rounded-2xl border border-white/10 bg-coal p-6 transition-colors hover:border-gold/50">
                    <Icon size={26} className="text-gold" />
                    <div className="mt-auto">
                      <p className="font-display text-base font-semibold leading-snug text-white sm:text-lg">{c.label}</p>
                      <p className="mt-1 text-xs font-medium text-slategray">{COUNTS[c.id] || 0} destinations</p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
            <Reveal delay={0.35}>
              <div className="flex h-full flex-col justify-between gap-6 rounded-2xl border border-gold/40 bg-gradient-to-br from-gold/15 to-transparent p-6">
                <Compass size={26} className="text-gold" />
                <div>
                  <p className="font-display text-base font-semibold text-white sm:text-lg">Can't decide?</p>
                  <p className="mt-1 text-xs text-mist">Tell us your dates — we'll suggest the route.</p>
                  <button onClick={() => openEnquiry("", "Category help card")} data-testid="home-help-choose-button" className="mt-3 text-xs font-bold uppercase tracking-wider text-gold hover:text-gold-light">Plan my trip →</button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <ErtigaSection />

      {/* Weekend trips teaser */}
      <section className="section-pad border-t border-white/5" data-testid="weekend-teaser-section">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <p className="eyebrow">Ready-to-Enquire Plans</p>
                <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">Family Holidays & Weekend Trips</h2>
                <p className="mt-4 leading-relaxed text-mist">Pick a route, choose 1-day to 7-day or customised, and we shape the plan around the season.</p>
              </div>
              <Link to="/weekend-trips" data-testid="all-itineraries-button" className="btn-ghost-gold w-fit shrink-0">All Itineraries <ArrowUpRight size={16} /></Link>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {ITINERARIES.slice(0, 3).map((it, i) => (
              <Reveal key={it.slug} delay={i * 0.07}>
                <Link to="/weekend-trips" data-testid={`weekend-teaser-${it.slug}`} className="group block overflow-hidden rounded-2xl border border-white/10 bg-coal card-lift">
                  <div className="relative aspect-[3/2] overflow-hidden">
                    <img src={img(it.images[0], 800)} alt={`${it.name} weekend trip itinerary`} loading="lazy" className="img-zoom h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg font-semibold text-white">{it.name}</h3>
                    <p className="mt-2 line-clamp-2 text-sm text-mist">{it.description}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact strip */}
      <section className="border-t border-gold/20 bg-coal/60 py-16" data-testid="home-contact-section">
        <div className="container-x grid gap-10 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow">Talk to the Mountains</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">One Call Starts the Journey</h2>
            <p className="mt-4 max-w-lg leading-relaxed text-mist">
              Share your dates and destination — we confirm the route, season notes and the plan before anything is booked.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a href={telLink(BUSINESS.phoneIndiaRaw)} data-testid="home-call-button" className="btn-gold"><Phone size={16} /> {BUSINESS.phoneIndia}</a>
              <a href={mailLink()} data-testid="home-email-button" className="btn-ghost-gold"><Mail size={16} /> Email Us</a>
              <a href={BUSINESS.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="home-instagram-button" className="btn-ghost-gold"><Instagram size={16} /> {BUSINESS.instagram}</a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/10 to-transparent p-8">
              <h3 className="font-display text-xl font-bold text-white">International Travellers</h3>
              <p className="mt-2 text-sm leading-relaxed text-mist">
                Calling from outside India? We answer on our international number and help with trip pacing, season advice and pickups from Dehradun, Haridwar or Rishikesh.
              </p>
              <a href={telLink(BUSINESS.phoneIntlRaw)} data-testid="home-call-intl-button" className="btn-white mt-6 w-full sm:w-auto">
                <Phone size={16} /> {BUSINESS.phoneIntl}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
